import React, { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import './PlaystyleTab.css';

interface PlaystyleTabProps {
  player: any;
  isOwner: boolean | null | string;
}

interface SliderDef {
  key: string;
  label: string;
  leftLabel: string;
  rightLabel: string;
  description: string;
  icon: string;
}

// Hitter sliders — 7 personality traits for position players
const HITTER_SLIDERS: SliderDef[] = [
  {
    key: 'powerFocus',
    label: 'Swing Intent',
    leftLabel: 'Contact',
    rightLabel: 'Power',
    description: 'High = swings for the fences (more HRs, more Ks). Low = short swing, puts the ball in play.',
    icon: '💥',
  },
  {
    key: 'swingAggression',
    label: 'Plate Aggression',
    leftLabel: 'Patient',
    rightLabel: 'Hacker',
    description: 'High = swings at the first good pitch. Low = works the count, forces the pitcher to throw strikes.',
    icon: '🪓',
  },
  {
    key: 'plateDiscipline',
    label: 'Pitch Selection',
    leftLabel: 'Chases Junk',
    rightLabel: 'Eagle Eye',
    description: 'High = walks a lot, rarely chases. Low = expands the zone, swings at bad pitches.',
    icon: '👁️',
  },
  {
    key: 'clutchFactor',
    label: 'Pressure Response',
    leftLabel: 'Fades Under Fire',
    rightLabel: 'Ice in Veins',
    description: 'High = performs better late & close, RISP. Low = small shrinks in big spots.',
    icon: '🧊',
  },
  {
    key: 'baserunningRisk',
    label: 'Baserunning Style',
    leftLabel: 'Station-to-Station',
    rightLabel: 'Hot Dog',
    description: 'High = takes extra bases, attempts steals, runs through stop signs. Low = conservative, plays it safe.',
    icon: '🏃',
  },
  {
    key: 'streakiness',
    label: 'Consistency',
    leftLabel: 'Machine',
    rightLabel: 'Streaky',
    description: 'High = goes hot and cold in big swings (hot streaks, bad slumps). Low = grinds out a steady line.',
    icon: '🎢',
  },
  {
    key: 'selfishness',
    label: 'Team Ethos',
    leftLabel: 'Team Player',
    rightLabel: 'Stat Hog',
    description: 'High = swings for self stats (HRs, avg), ignores situational hitting. Low = lays down bunts, hits the cutoff, sacrifices for the win.',
    icon: '🏆',
  },
];

// Pitcher sliders — 7 personality traits for pitchers
const PITCHER_SLIDERS: SliderDef[] = [
  {
    key: 'pitchApproach',
    label: 'Pitching Philosophy',
    leftLabel: 'K Artist',
    rightLabel: 'Weak Contact',
    description: 'K artists go for the punchout — high K rate but more walks, higher pitch counts, allow harder contact. Weak contact pitchers induce grounders and pop-ups — fewer Ks but lower BABIP, more efficient.',
    icon: '🔥',
  },
  {
    key: 'zoneAttack',
    label: 'Zone Strategy',
    leftLabel: 'Nibbler',
    rightLabel: 'Attack Zone',
    description: 'Nibblers paint the edges — more walks but fewer hittable pitches, fewer HRs. Zone attackers challenge hitters — fewer walks but more hittable pitches, more looking Ks.',
    icon: '🎯',
  },
  {
    key: 'pitchSelection',
    label: 'Arsenal Depth',
    leftLabel: 'One-Pitch Wonder',
    rightLabel: 'Arsenal Master',
    description: 'One-pitch wonders are predictable — less effective through the order, higher BABIP. Arsenal masters mix speeds and locations — more Ks, weaker contact, stays effective deeper.',
    icon: '🃏',
  },
  {
    key: 'clutchPitching',
    label: 'Pressure Response',
    leftLabel: 'Fades Under Fire',
    rightLabel: 'Ice in Veins',
    description: 'Ice-cold pitchers shut down opposing clutch situations. Those who fade give up bigger hits when runners are on and the game is tight.',
    icon: '🧊',
  },
  {
    key: 'workhorseMentality',
    label: 'Stamina Management',
    leftLabel: 'Pace Setter',
    rightLabel: 'Grind It Out',
    description: 'Pace setters are dominant early but decline fast when tired. Grinders maintain effectiveness deep into games — they lose less velocity and command as stamina drops.',
    icon: '🐎',
  },
  {
    key: 'streakiness',
    label: 'Consistency',
    leftLabel: 'Machine',
    rightLabel: 'Streaky',
    description: 'Streaky pitchers run hot and cold — dominant one start, shelled the next. Machines grind out the same steady performance every time out.',
    icon: '🎢',
  },
  {
    key: 'selfishness',
    label: 'Team Ethos',
    leftLabel: 'Team Player',
    rightLabel: 'Stat Hog',
    description: 'Team players pitch to contact with leads — sacrifice ERA for outs to preserve wins. Stat hogs nibble to protect their numbers — fewer hits allowed but more walks and higher pitch counts.',
    icon: '🏆',
  },
];

const DEFAULT_HITTER_PERSONALITY = {
  powerFocus: 50,
  swingAggression: 50,
  plateDiscipline: 50,
  clutchFactor: 50,
  baserunningRisk: 50,
  streakiness: 50,
  selfishness: 50,
};

const DEFAULT_PITCHER_PERSONALITY = {
  pitchApproach: 50,
  zoneAttack: 50,
  pitchSelection: 50,
  clutchPitching: 50,
  workhorseMentality: 50,
  streakiness: 50,
  selfishness: 50,
};

export default function PlaystyleTab({ player, isOwner }: PlaystyleTabProps) {
  const isPitcher = player.isHitter === false || (!player.isHitter && player.is_hitter === false);
  // Fallback: if isHitter isn't set, check personality keys to infer
  const personalityKeys = player.personality ? Object.keys(player.personality) : [];
  const hasPitcherKeys = personalityKeys.some(k => ['pitchApproach', 'zoneAttack', 'pitchSelection', 'clutchPitching', 'workhorseMentality'].includes(k));
  const finalIsPitcher = personalityKeys.length > 0 ? hasPitcherKeys : isPitcher;

  const sliders = finalIsPitcher ? PITCHER_SLIDERS : HITTER_SLIDERS;
  const defaultPersonality = finalIsPitcher ? DEFAULT_PITCHER_PERSONALITY : DEFAULT_HITTER_PERSONALITY;

  const [personality, setPersonality] = useState<Record<string, number>>(
    player.personality || defaultPersonality
  );
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setPersonality(player.personality || defaultPersonality);
    setDirty(false);
  }, [player.id]);

  const handleChange = (key: string, value: number) => {
    setPersonality(prev => ({ ...prev, [key]: value }));
    setDirty(true);
    setSaveMsg(null);
  };

  const handleSave = async () => {
    if (!isOwner) return;
    setSaving(true);
    setSaveMsg(null);
    try {
      const { error } = await supabase
        .from('players')
        .update({ personality })
        .eq('id', player.id);
      if (error) throw error;
      setSaveMsg('Saved — next game will reflect changes');
      setDirty(false);
      // Clear msg after 3s
      setTimeout(() => setSaveMsg(null), 3000);
    } catch (err: any) {
      setSaveMsg(`Error: ${err.message || 'save failed'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setPersonality(player.personality || defaultPersonality);
    setDirty(false);
    setSaveMsg(null);
  };

  return (
    <div className="playstyle-tab">
      <div className="playstyle-header">
        <h3>{finalIsPitcher ? 'Mound Personality' : 'Playstyle & Personality'}</h3>
        <p className="playstyle-subtitle">
          {finalIsPitcher
            ? 'These sliders shape how this pitcher approaches the mound. K artist or weak contact specialist? Nibbler or zone attacker? Tune the dials to build your ace.'
            : 'These sliders affect how this player approaches the game. Each batter plays differently — adjust the dials to tune this monster\'s style.'}
        </p>
      </div>

      <div className="playstyle-sliders">
        {sliders.map(s => {
          const val = personality[s.key] ?? 50;
          return (
            <div key={s.key} className="playstyle-slider-row">
              <div className="playstyle-slider-header">
                <span className="playstyle-slider-icon">{s.icon}</span>
                <span className="playstyle-slider-label">{s.label}</span>
                <span className="playstyle-slider-value">{val}</span>
              </div>
              <div className="playstyle-slider-track">
                <span className="playstyle-slider-leftlabel">{s.leftLabel}</span>
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={val}
                  onChange={e => handleChange(s.key, Number(e.target.value))}
                  disabled={!isOwner}
                  className="playstyle-slider-input"
                />
                <span className="playstyle-slider-rightlabel">{s.rightLabel}</span>
              </div>
              <div className="playstyle-slider-desc">{s.description}</div>
            </div>
          );
        })}
      </div>

      {isOwner ? (
        <div className="playstyle-actions">
          <button
            className="playstyle-save-btn"
            onClick={handleSave}
            disabled={!dirty || saving}
          >
            {saving ? 'Saving...' : dirty ? '💾 Save Playstyle' : 'Saved'}
          </button>
          <button
            className="playstyle-reset-btn"
            onClick={handleReset}
            disabled={!dirty || saving}
          >
            Reset
          </button>
          {saveMsg && <span className="playstyle-msg">{saveMsg}</span>}
        </div>
      ) : (
        <div className="playstyle-readonly">
          🔒 You don't own this player. Sliders are read-only.
        </div>
      )}
    </div>
  );
}
